/**
 * worldDxfExporter — general WGS84 → local-metre DXF for studio/site
 * geometry (distinct from cadDxfExporter, which round-trips imported CAD
 * back into its ORIGINAL drawing frame).
 *
 * Frame: equirectangular metres about the bundle's anchor (x = east,
 * y = north), matching the solver's local-axes convention at rotation 0.
 * Entities: LWPOLYLINE (closed for polygons) and POINT, one DXF layer per
 * `<category>_<kind>` pair. `$INSUNITS = 6` (metres). The header carries the
 * anchor so the frame is reconstructible.
 */
import type { LayoutExportBundle, LayoutExportFeature } from './layoutExportBundle';

const EARTH_RADIUS_M = 6_378_137;
const MIN_COS_LATITUDE = 1e-8;
const DXF_LINE_ENDING = '\r\n';

function pair(code: number, value: string | number): string[] {
  return [String(code), String(value)];
}

function dxfNumber(value: number): string {
  if (!Number.isFinite(value)) throw new Error('DXF export encountered a non-finite coordinate.');
  return Number(value.toFixed(6)).toString();
}

function dxfLayerName(feature: LayoutExportFeature): string {
  const raw = `${feature.category}_${feature.kind}`.toUpperCase();
  const cleaned = raw.replace(/[^A-Z0-9_-]+/g, '_').replace(/^_+|_+$/g, '');
  return (cleaned || 'EXPORT').slice(0, 255);
}

function toLocalMetres(
  point: { lat: number; lng: number },
  anchor: { lat: number; lng: number },
): { xM: number; yM: number } {
  const anchorLatRad = anchor.lat * Math.PI / 180;
  return {
    xM: (point.lng - anchor.lng) * Math.PI / 180
      * EARTH_RADIUS_M
      * Math.max(MIN_COS_LATITUDE, Math.abs(Math.cos(anchorLatRad))),
    yM: (point.lat - anchor.lat) * Math.PI / 180 * EARTH_RADIUS_M,
  };
}

export interface ExportedWorldDxf {
  dxf: string;
  filename: string;
  anchor: { lat: number; lng: number };
  entityCount: number;
  layerNames: string[];
}

/** Prefer the bundle's reviewed origin; fall back to the deterministic vertex mean. */
export function bundleAnchor(bundle: LayoutExportBundle): { lat: number; lng: number } | null {
  const manifestOrigin = bundle.manifest.origin;
  if (
    manifestOrigin
    && Number.isFinite(manifestOrigin.lat)
    && Number.isFinite(manifestOrigin.lng)
  ) {
    return { ...manifestOrigin };
  }
  const points = bundle.features.flatMap(feature => feature.geometry.coordinates)
    .filter(point => Number.isFinite(point.lat) && Number.isFinite(point.lng));
  if (points.length === 0) return null;
  const sum = points.reduce(
    (total, point) => ({ lat: total.lat + point.lat, lng: total.lng + point.lng }),
    { lat: 0, lng: 0 },
  );
  return { lat: sum.lat / points.length, lng: sum.lng / points.length };
}

export function exportBundleAsWorldDxf(bundle: LayoutExportBundle): ExportedWorldDxf {
  const anchor = bundleAnchor(bundle);
  if (!anchor) throw new Error('Nothing to export: the bundle has no drawable features.');

  const layerNames = [...new Set(bundle.features.map(dxfLayerName))].sort();
  const rows: string[] = [];

  rows.push(...pair(0, 'SECTION'), ...pair(2, 'HEADER'));
  rows.push(...pair(9, '$ACADVER'), ...pair(1, 'AC1015'));
  rows.push(...pair(9, '$INSUNITS'), ...pair(70, 6));
  rows.push(...pair(0, 'ENDSEC'));

  rows.push(...pair(0, 'SECTION'), ...pair(2, 'TABLES'));
  rows.push(...pair(0, 'TABLE'), ...pair(2, 'LAYER'), ...pair(70, layerNames.length));
  for (const layerName of layerNames) {
    rows.push(
      ...pair(0, 'LAYER'),
      ...pair(2, layerName),
      ...pair(70, 0),
      ...pair(62, 7),
      ...pair(6, 'CONTINUOUS'),
    );
  }
  rows.push(...pair(0, 'ENDTAB'), ...pair(0, 'ENDSEC'));

  let entityCount = 0;
  rows.push(...pair(0, 'SECTION'), ...pair(2, 'ENTITIES'));
  for (const feature of bundle.features) {
    const points = feature.geometry.coordinates
      .filter(point => Number.isFinite(point.lat) && Number.isFinite(point.lng))
      .map(point => toLocalMetres(point, anchor));
    const layerName = dxfLayerName(feature);
    rows.push(...pair(
      999,
      `datacrate-feature id=${feature.id} source=${feature.source} properties=${JSON.stringify(feature.properties ?? {})}`,
    ));
    if (feature.geometry.type === 'point') {
      const point = points[0];
      if (!point) continue;
      rows.push(
        ...pair(0, 'POINT'),
        ...pair(8, layerName),
        ...pair(10, dxfNumber(point.xM)),
        ...pair(20, dxfNumber(point.yM)),
        ...pair(30, 0),
      );
      entityCount += 1;
      continue;
    }
    // Polygons write their OPEN ring with the closed flag; duplicated
    // closing vertices are dropped so vertex counts stay honest.
    const open = feature.geometry.type === 'polygon'
      && points.length >= 2
      && points[0]!.xM === points.at(-1)!.xM
      && points[0]!.yM === points.at(-1)!.yM
      ? points.slice(0, -1)
      : points;
    const distinctPointCount = new Set(
      open.map(point => `${point.xM.toFixed(6)},${point.yM.toFixed(6)}`),
    ).size;
    if (
      (feature.geometry.type === 'polygon' && distinctPointCount < 3)
      || (feature.geometry.type === 'line' && distinctPointCount < 2)
      || open.length < 2
    ) continue;
    rows.push(
      ...pair(0, 'LWPOLYLINE'),
      ...pair(8, layerName),
      ...pair(90, open.length),
      ...pair(70, feature.geometry.type === 'polygon' ? 1 : 0),
    );
    for (const point of open) {
      rows.push(...pair(10, dxfNumber(point.xM)), ...pair(20, dxfNumber(point.yM)));
    }
    entityCount += 1;
    if (feature.geometry.type === 'polygon' && feature.geometry.holes?.length) {
      for (const hole of feature.geometry.holes) {
        const holePts = hole
          .filter(point => Number.isFinite(point.lat) && Number.isFinite(point.lng))
          .map(point => toLocalMetres(point, anchor));
        const holeOpen = holePts.length >= 2
          && holePts[0]!.xM === holePts.at(-1)!.xM
          && holePts[0]!.yM === holePts.at(-1)!.yM
          ? holePts.slice(0, -1)
          : holePts;
        const holeDistinct = new Set(
          holeOpen.map(point => `${point.xM.toFixed(6)},${point.yM.toFixed(6)}`),
        ).size;
        if (holeDistinct < 3 || holeOpen.length < 3) continue;
        rows.push(
          ...pair(0, 'LWPOLYLINE'),
          ...pair(8, layerName),
          ...pair(90, holeOpen.length),
          ...pair(70, 1),
        );
        for (const point of holeOpen) {
          rows.push(...pair(10, dxfNumber(point.xM)), ...pair(20, dxfNumber(point.yM)));
        }
        entityCount += 1;
      }
    }
  }
  rows.push(...pair(0, 'ENDSEC'), ...pair(0, 'EOF'));

  if (entityCount === 0) throw new Error('Nothing to export: the bundle has no drawable features.');

  return {
    // Anchor comment first: 999 lines are ignored by readers but keep the
    // local frame reconstructible.
    dxf: [
      '999',
      `datacrate-world-dxf schema=${bundle.schemaVersion} anchor=${anchor.lat.toFixed(8)},${anchor.lng.toFixed(8)} frame=equirectangular-metres manifest=${JSON.stringify(bundle.manifest)}`,
      ...rows,
    ].join(DXF_LINE_ENDING) + DXF_LINE_ENDING,
    filename: `${bundle.name}.dxf`,
    anchor,
    entityCount,
    layerNames,
  };
}
